/**
 * Filter Sidebar Component
 * 
 * Reusable filter sidebar that slides in from the right side
 * Used for Campaigns, Promos, and other listing pages
 */

import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import './FilterSidebar.css';

// Common styling constants for react-select
const FILTER_HEIGHT = '38px';
const FILTER_FONT_SIZE = '14px';
const BORDER_COLOR = '#e3e6f0';

// Common react-select styles matching seller filters
const getSelectStyles = () => ({
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any, state: any) => ({
    ...base,
    height: FILTER_HEIGHT,
    minHeight: FILTER_HEIGHT,
    border: `1px solid ${BORDER_COLOR}`,
    borderRadius: '6px',
    fontSize: FILTER_FONT_SIZE,
    boxShadow: 'none',
    width: '100%',
    minWidth: '150px',
    borderColor: BORDER_COLOR,
    '&:hover': {
      borderColor: BORDER_COLOR
    }
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: '36px',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center'
  }),
  input: (base: any) => ({
    ...base,
    margin: '0',
    paddingTop: '0',
    paddingBottom: '0',
    height: 'auto'
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '36px',
    padding: '0 8px'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    padding: '0',
    svg: {
      width: '16px',
      height: '16px'
    }
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#212529',
    fontSize: FILTER_FONT_SIZE,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 20px)'
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#6c757d',
    fontSize: FILTER_FONT_SIZE
  }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected 
      ? '#e9ecef'
      : state.isFocused 
        ? '#f8f9fa'
        : 'white',
    color: '#212529',
    fontSize: FILTER_FONT_SIZE,
    '&:hover': {
      backgroundColor: '#f8f9fa'
    }
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999
  })
});

export interface FilterSidebarProps {
  show: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  onReset: () => void;
  filters: FilterValues;
  filterConfig: FilterConfig;
  onFilterChange?: (key: string, value: string) => void;
}

export interface FilterValues {
  domain?: string;
  status?: string;
  campaignType?: string;
  startDate?: string;
  endDate?: string;
  [key: string]: string | undefined;
}

export interface FilterConfig {
  domain?: {
    label: string;
    options: { value: string; label: string }[];
  };
  status?: {
    label: string;
    options: { value: string; label: string }[];
  };
  campaignType?: {
    label: string;
    options: { value: string; label: string }[];
  };
  transactionType?: {
    label: string;
    options: { value: string; label: string }[];
  };
  promoId?: {
    label: string;
    options: { value: string; label: string }[];
  };
  action?: {
    label: string;
    options: { value: string; label: string }[];
  };
  dateRange?: {
    label: string;
    startDateLabel: string;
    endDateLabel: string;
  };
  [key: string]: any; // Allow additional filter types
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  show,
  onClose,
  onApply,
  onReset,
  filters,
  filterConfig,
  onFilterChange,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterValues>(filters);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);
  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && show) {
        onClose();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when sidebar is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [show, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };


  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...localFilters, [key]: value === 'all' || value === '' ? undefined : value };
    setLocalFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(key, value === 'all' || value === '' ? '' : value);
    }
  };

  const handleApply = () => {
    // Validate date range before applying
    if (localFilters.startDate && localFilters.endDate && localFilters.endDate < localFilters.startDate) {
      // Don't apply filters if date range is invalid
      // Validation error is already shown in the UI via the error message
      return;
    }
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    const resetFilters: FilterValues = {};
    setLocalFilters(resetFilters);
    onReset();
  };

  return (
    <>
      {/* Backdrop */}
      {show && (
        <div
          className="filter-sidebar-backdrop"
          onClick={handleBackdropClick}
        />
      )}

      {/* Sidebar */}
      <div className={`filter-sidebar ${show ? 'show' : ''}`}>
        <div className="filter-sidebar-content">
          {/* Header */}
          <div className="filter-sidebar-header">
            <h5 className="filter-sidebar-title">Filters</h5>
            <button
              type="button"
              className="filter-sidebar-close"
              onClick={onClose}
              aria-label="Close filters"
            >
              <i className="ph-x"></i>
            </button>
          </div>

          {/* Filter Content */}
          <div className="filter-sidebar-body">
            {/* Domain Filter */}
            {filterConfig.domain && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.domain.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.domain.options.find(opt => opt.value === (localFilters.domain || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('domain', selectedOption?.value || 'all')}
                  options={filterConfig.domain.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Status Filter */}
            {filterConfig.status && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.status.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.status.options.find(opt => opt.value === (localFilters.status || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('status', selectedOption?.value || 'all')}
                  options={filterConfig.status.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Campaign Type Filter */}
            {filterConfig.campaignType && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.campaignType.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.campaignType.options.find(opt => opt.value === (localFilters.campaignType || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('campaignType', selectedOption?.value || 'all')}
                  options={filterConfig.campaignType.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Transaction Type Filter */}
            {filterConfig.transactionType && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.transactionType.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.transactionType.options.find(opt => opt.value === (localFilters.transactionType || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('transactionType', selectedOption?.value || 'all')}
                  options={filterConfig.transactionType.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Promo ID Filter */}
            {filterConfig.promoId && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.promoId.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.promoId.options.find((opt: { value: string; label: string }) => opt.value === (localFilters.promoId || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('promoId', selectedOption?.value || 'all')}
                  options={filterConfig.promoId.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Action Filter */}
            {filterConfig.action && (
              <div className="filter-group">
                <Form.Label className="filter-label">
                  {filterConfig.action.label}
                </Form.Label>
                <Select
                  classNamePrefix="react-select"
                  value={filterConfig.action.options.find((opt: { value: string; label: string }) => opt.value === (localFilters.action || 'all'))}
                  onChange={(selectedOption: any) => handleFilterChange('action', selectedOption?.value || 'all')}
                  options={filterConfig.action.options}
                  menuPortalTarget={document.body}
                  styles={getSelectStyles()}
                />
              </div>
            )}

            {/* Date Range Filter */}
            {filterConfig.dateRange && (
              <div className="filter-group">
                <Form.Label className="filter-label filter-section-label">
                  {filterConfig.dateRange.label}
                </Form.Label>
                
                <div className="filter-date-group">
                  <div className="filter-date-item">
                    <Form.Label className="filter-date-label">
                      {filterConfig.dateRange.startDateLabel}
                    </Form.Label>
                    <div className="filter-date-input-wrapper">
                      <Form.Control
                        type="date"
                        value={localFilters.startDate || ''}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          handleFilterChange('startDate', newStartDate);
                          // If end date is before new start date, clear it
                          if (localFilters.endDate && newStartDate && localFilters.endDate < newStartDate) {
                            handleFilterChange('endDate', '');
                          }
                        }}
                        max={localFilters.endDate || undefined}
                        className="filter-date-input"
                        onFocus={(e) => {
                          e.target.style.borderColor = '#E5E7EB';
                          e.target.style.boxShadow = 'none';
                          e.target.style.outline = 'none';
                        }}
                      />
                      <i className="ph-calendar filter-date-icon"></i>
                    </div>
                  </div>

                  <div className="filter-date-item">
                    <Form.Label className="filter-date-label">
                      {filterConfig.dateRange.endDateLabel}
                    </Form.Label>
                    <div className="filter-date-input-wrapper">
                      <Form.Control
                        type="date"
                        value={localFilters.endDate || ''}
                        onChange={(e) => {
                          const newEndDate = e.target.value;
                          // Validate that end date is not before start date
                          if (localFilters.startDate && newEndDate && newEndDate < localFilters.startDate) {
                            // Show error feedback
                            e.target.setCustomValidity('End date must be after start date');
                            e.target.reportValidity();
                            return;
                          }
                          e.target.setCustomValidity('');
                          handleFilterChange('endDate', newEndDate);
                        }}
                        min={localFilters.startDate || undefined}
                        className="filter-date-input"
                        onFocus={(e) => {
                          e.target.style.borderColor = '#E5E7EB';
                          e.target.style.boxShadow = 'none';
                          e.target.style.outline = 'none';
                        }}
                      />
                      <i className="ph-calendar filter-date-icon"></i>
                    </div>
                    {localFilters.startDate && localFilters.endDate && localFilters.endDate < localFilters.startDate && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                        End date must be after start date
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="filter-sidebar-footer">
            <button
              type="button"
              className="filter-reset-btn"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="filter-apply-btn"
              onClick={handleApply}
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterSidebar;
